import type { ActionItem, Newsletter, NewsletterVersion } from '@/types';
import { SAMPLE_ACTION_ITEMS, SAMPLE_NEWSLETTERS, SAMPLE_VERSIONS } from '@/constants/sampleData';

/**
 * Storage abstraction. All persistence goes through this async interface so the
 * browser-storage implementation below can later be swapped for a Cloudflare
 * D1 (SQL) or KV-backed adapter without touching services, slices, or components.
 */
export interface StorageAdapter<T extends { id: string }> {
  getAll(): Promise<T[]>;
  getById(id: string): Promise<T | undefined>;
  save(item: T): Promise<void>;
  saveMany(items: T[]): Promise<void>;
  remove(id: string): Promise<void>;
}

/** localStorage-backed adapter. Ships today; same interface will later be
 * satisfied by a Cloudflare D1 or KV client for multi-device persistence. */
export class LocalStorageAdapter<T extends { id: string }> implements StorageAdapter<T> {
  constructor(private readonly key: string) {}

  private readAll(): T[] {
    const raw = window.localStorage.getItem(this.key);
    if (!raw) return [];
    try {
      return JSON.parse(raw) as T[];
    } catch {
      return [];
    }
  }

  private writeAll(items: T[]): void {
    window.localStorage.setItem(this.key, JSON.stringify(items));
  }

  async getAll(): Promise<T[]> {
    return this.readAll();
  }

  async getById(id: string): Promise<T | undefined> {
    return this.readAll().find((item) => item.id === id);
  }

  async save(item: T): Promise<void> {
    const items = this.readAll();
    const index = items.findIndex((existing) => existing.id === item.id);
    if (index === -1) {
      items.push(item);
    } else {
      items[index] = item;
    }
    this.writeAll(items);
  }

  async saveMany(newItems: T[]): Promise<void> {
    const items = this.readAll();
    for (const item of newItems) {
      const index = items.findIndex((existing) => existing.id === item.id);
      if (index === -1) {
        items.push(item);
      } else {
        items[index] = item;
      }
    }
    this.writeAll(items);
  }

  async remove(id: string): Promise<void> {
    this.writeAll(this.readAll().filter((item) => item.id !== id));
  }

  /** Only used for first-run demo seeding. */
  seedIfEmpty(seed: T[]): void {
    if (window.localStorage.getItem(this.key) === null) {
      this.writeAll(seed);
    }
  }
}

const STORAGE_KEYS = {
  newsletters: 'pulseai.newsletters',
  actionItems: 'pulseai.actionItems',
  versions: 'pulseai.versions',
} as const;

export const newslettersStorage = new LocalStorageAdapter<Newsletter>(STORAGE_KEYS.newsletters);
export const actionItemsStorage = new LocalStorageAdapter<ActionItem>(STORAGE_KEYS.actionItems);
export const versionsStorage = new LocalStorageAdapter<NewsletterVersion>(STORAGE_KEYS.versions);

/** Seeds demo data on first run only; no-ops once the user has real data. */
export function seedDemoDataIfEmpty(): void {
  newslettersStorage.seedIfEmpty(SAMPLE_NEWSLETTERS);
  actionItemsStorage.seedIfEmpty(SAMPLE_ACTION_ITEMS);
  versionsStorage.seedIfEmpty(SAMPLE_VERSIONS);
}
